import React from "react";
import { Avatar, Button, Popover, Tooltip } from "antd";
import {
  QuestionCircleOutlined,
  PhoneOutlined,
  EyeOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { capitalize } from "lodash";
import { useSelector } from "react-redux";
import { getCacheApi } from "../../../redux/slices/apiCacheSlice";
import { EAI_LOGO_GRAPHIC_ONLY } from "../../../config";
import { convertUtcToLocal } from "../../../library/utils";
import { convertToInitials } from "../../layoutComponent/layoutHelpers";
import MessageTypeLabel from "./MessageTypeLabel";
import { formatMessageType } from "../../../utils/formatters";
import PinnedMessageTag from "./PinMessages/PinnedMessageTag";
import UserHoverCard from "./PinMessages/UserHoverCard";
import { renderEmailStatus } from "../../../constant/EmailConstant";
import AiFeedbackButtons from "./AiFeedbackButtons";
import { useOpportunityBadge } from "../../../hooks/useOpportunityBadge";

/**
 * Reusable message header component for all message types (SMS, MMS, Email, Call).
 *
 * Renders: Avatar, timestamp, username/lead name, message type icon,
 * campaign tag, pinned message tag, otto reason, email status.
 *
 * Direction determines layout:
 * - TO_LEAD: content right-aligned, avatar on right
 * - FROM_LEAD: content left-aligned, avatar on left
 */
const MessageMetaInfo = ({
  // Direction & type
  direction, // "TO_LEAD" | "FROM_LEAD" | "TO_USER" | "FROM_OTTO"
  messageType, // "SMS" | "MMS" | "EMAIL" | "CALL" | "WIDGET_CHAT" etc.
  contentType, // for AI Response detection

  // Avatar
  avatarUser, // { fullName, profilePic } - for TO_LEAD avatar
  leadName, // string - for FROM_LEAD avatar initials
  showTranslationIcon = false,
  RenderTranslation,

  // Timestamp
  timestamp, // UTC date string
  timestampFormat = "h:mm A",
  timestampFullFormat = "ddd, MMM Do YYYY, h:mm A",

  // Name display
  userName, // for TO_LEAD direction
  displayName, // override name (used by calls)

  // Phone tooltip
  RenderPhone,
  phoneNumber = "",
  phoneDirection, // override direction for tooltip
  phoneLabel, // "Email" | "Call" | undefined (default message)
  phoneSentLabel, // custom label like "made to"
  phoneReceiveLabel, // custom label like "received from"
  tooltipClassName,

  // Optional features
  campaign, // { name, campaignType } | null
  isMessagePinned = false,
  messageNotesCount = 0,
  messageId,
  messageContent,
  messageUser, // user object for PinnedMessageTag and UserHoverCard (display user)
  messageUserId, // number|null — the message's userId (the display / reply-as user)
  // Reply-as (EAI-1530): id of the ACTUAL sender (the "original" user) when the
  // message was sent on behalf of someone. The message is DISPLAYED as `user`
  // (userId); this original sender is shown as a small "on behalf of" avatar
  // below it. Resolved from the userList cache.
  originalUserId, // number|null
  originalUserName, // string|"" — fallback name (from userIdMap) when the original
  // sender isn't in the userList cache (e.g. a support account)
  reasonForResponse, // string | null
  hasAnyForwardOption = false,
  isAutoResponder = false, // userName === lang.autoResponderUserName

  // Email specific
  emailStatus, // string | null
  EmailDetailsPopover, // ReactNode - Popover content for email icon

  // Opp-type badge — small root opp_type icon (Sales/Service/Parts/etc.)
  // shown next to the message-type icon. Resolved HERE, once, from raw
  // data every caller already has — callers never compute this
  // themselves, so a future icon/behavior change only touches this file.
  // ``leadOpportunities`` is the lead's full opp list (has each opp's
  // opportunityType chain); ``leadOpportunityId`` is the scalar id off
  // the message/call being rendered.
  leadOpportunityId = null,
  leadOpportunities = null,

  // Call specific
  showPhoneIcon = false, // true for calls - renders PhoneOutlined instead of MessageTypeLabel

  // Footer action buttons
  aiFeedbackProps, // { feedback, messageFeedback, isFeedbackSent, onFeedback }
  showSentButton = false, // show "Sent" media tracking button
  onSentClick, // callback for Sent button
  showRetry = false, // show retry button
  RetryComponent, // component to render for retry

  // Sending state (Phase 3a: lambda cutover renders the bubble optimistically
  // before the persisted Message exists; this prop fades the content + shows
  // a small "Sending…" footer until the SocketIO sent_message event arrives).
  isSending = false,

  // Children (for translation icon or extra elements)
  children,
}) => {
  const isSent = direction === "TO_LEAD";
  const isReceived = direction === "FROM_LEAD" || direction === "TO_USER";

  // Reply-as (EAI-1530): resolve the ACTUAL sender ("original" user) from the
  // userList cache. The message is DISPLAYED as `user` (userId); this original
  // sender is the small "on behalf of" badge shown when it differs from userId.
  const { users: cachedUsers } = useSelector(state => getCacheApi(state, "userList")) || {};
  const originalRaw = originalUserId
    ? (cachedUsers || []).find(u => +u?.id === +originalUserId)
    : null;
  const originalUser = originalRaw
    ? {
        id: originalRaw.id,
        fullName:
          originalRaw.fullName ||
          `${originalRaw.firstName || ""} ${originalRaw.lastName || ""}`.trim(),
        profilePic: originalRaw.profilePic,
      }
    : null;
  // On-behalf whenever originalUserId is present and differs from the displayed
  // user (userId) — decided by id, NOT by cache resolution, so the badge still
  // shows when the original sender isn't in the userList cache (e.g. a system /
  // support account). The name/photo are best-effort from cache.
  const isOnBehalf = !!originalUserId && +originalUserId !== +(messageUserId ?? NaN);
  // Main (display) identity = the message's userId. On a reply-as send the
  // backend populates message.user with the AUTHENTICATED (login) user, not the
  // reply-as user, so `avatarUser` (built from message.user) is unreliable here.
  // Resolve from the userList cache by messageUserId first (the reply-as user is
  // always in that cache — it was selectable in the picker), then fall back to
  // avatarUser / userName for normal sends and uncached senders.
  const mainRaw = messageUserId ? (cachedUsers || []).find(u => +u?.id === +messageUserId) : null;
  const resolvedMainName = mainRaw
    ? mainRaw.fullName || `${mainRaw.firstName || ""} ${mainRaw.lastName || ""}`.trim()
    : "";
  const displayUser = {
    fullName: resolvedMainName || avatarUser?.fullName || userName || "",
    profilePic: mainRaw?.profilePic || avatarUser?.profilePic || null,
  };
  const onBehalfOfUser = isOnBehalf
    ? {
        id: originalUser?.id || originalUserId,
        // prefer the cache, then the name resolved upstream from userIdMap, then empty
        fullName: originalUser?.fullName || originalUserName || "",
        profilePic: originalUser?.profilePic || null,
      }
    : null; // secondary = actual sender (falls back to a nameless avatar if uncached)
  const isAiResponse = contentType === "AI Response To Lead";
  const hasCampaign = campaign && campaign.name && campaign.name !== "Default";
  const hasTags = hasCampaign || isMessagePinned;

  // Opp-type badge — single source of truth lives in useOpportunityBadge
  // (resolution, icon, tooltip, and the "All opportunities only" gate).
  const opportunityBadge = useOpportunityBadge(leadOpportunityId, leadOpportunities);

  // Resolve the phone tooltip direction
  const resolvedPhoneDirection = phoneDirection || direction;

  // Build the phone tooltip content
  const PhoneTooltipContent = phoneNumber ? (
    <RenderPhone
      direction={resolvedPhoneDirection === "TO_USER" ? "FROM_LEAD" : resolvedPhoneDirection}
      messagephone={phoneNumber}
      {...(phoneLabel && { messageLabel: phoneLabel })}
      {...(phoneSentLabel && { messageSentLabel: phoneSentLabel })}
      {...(phoneReceiveLabel && { messageReceiveLabel: phoneReceiveLabel })}
      {...(tooltipClassName && { tooltipClassName })}
    />
  ) : null;

  // ---------- Avatar ----------
  const renderAvatar = () => {
    if (isSent) {
      const primaryAvatar = (
        <Avatar
          src={isAiResponse ? EAI_LOGO_GRAPHIC_ONLY : displayUser?.profilePic || undefined}
          className={`${
            isAiResponse || displayUser?.profilePic?.length > 0 ? "" : "bg-[#f6df9c]"
          } h-6 w-6 flex-row-centered items-center justify-center`}
        >
          <span className="text-[#64748B]">{convertToInitials(displayUser?.fullName)}</span>
        </Avatar>
      );

      // Reply-as (EAI-1530): when sent on behalf of another user, show the actual
      // sender as a SECOND full-size avatar stacked vertically below the first,
      // with an "on behalf of" tooltip on hover. Shown whenever there's an
      // on-behalf sender, even if their name isn't cached.
      if (onBehalfOfUser) {
        const behalfName = onBehalfOfUser.fullName || "";
        const behalfInitials = convertToInitials(behalfName);
        return (
          <span className="inline-flex flex-col items-center gap-1">
            {primaryAvatar}
            <Tooltip
              title={
                behalfName
                  ? `${behalfName} replied on behalf of ${displayUser?.fullName || ""}`
                  : `Replied on behalf of ${displayUser?.fullName || ""}`
              }
            >
              <Avatar
                src={onBehalfOfUser.profilePic || undefined}
                icon={!onBehalfOfUser.profilePic && !behalfInitials ? <UserOutlined /> : undefined}
                className={`${
                  onBehalfOfUser.profilePic ? "" : "bg-[#37C6C0]"
                } h-6 w-6 flex-row-centered items-center justify-center text-white`}
              >
                {!onBehalfOfUser.profilePic && behalfInitials ? (
                  <span className="text-white">{behalfInitials}</span>
                ) : null}
              </Avatar>
            </Tooltip>
          </span>
        );
      }
      return primaryAvatar;
    }

    // FROM_LEAD avatar
    return (
      <Avatar className="bg-error-400 flex-row-centered text-grey-200 text-[9.6px] font-[600] items-center justify-center h-6 w-6">
        {convertToInitials(leadName)}
      </Avatar>
    );
  };

  // ---------- Message Type Icon ----------
  const renderMessageTypeIcon = () => {
    if (showPhoneIcon) {
      return (
        <Tooltip title={capitalize(messageType || "")}>
          <PhoneOutlined className="text-grey-500 rotate-90" />
        </Tooltip>
      );
    }

    if (EmailDetailsPopover) {
      return (
        <Popover
          trigger={["hover"]}
          title={formatMessageType(messageType)}
          content={EmailDetailsPopover}
        >
          <span>
            <MessageTypeLabel
              messageType={messageType}
              placement="bottom"
              defaultIcon="mail"
              showTooltip={false}
              containerClassName={isSent ? "flex mt-0" : "-mb-1"}
              {...(!isSent && { iconClassName: "text-grey-500 ml-1" })}
            />
          </span>
        </Popover>
      );
    }

    return <MessageTypeLabel containerClassName="-mb-1" messageType={messageType} />;
  };

  // ---------- Username (TO_LEAD) ----------
  const renderUserName = () => {
    const nameClasses = "text-grey-700 line-clamp-1 break-all max-w-[180px] text-xs font-[600]";
    const nameWithHover = `${nameClasses} cursor-pointer hover:text-indigo-600 transition-colors`;
    // Prefer the cache-resolved display (reply-as) user over the passed userName,
    // which can fall back to the login user when userId isn't in userIdMap.
    // Auto-responder/AI keeps its own label.
    const resolvedName = displayName || (isAutoResponder ? userName : resolvedMainName || userName);

    if (isAutoResponder) {
      return (
        <Tooltip title={PhoneTooltipContent}>
          <span className={nameClasses}>{resolvedName}</span>
        </Tooltip>
      );
    }

    if (hasAnyForwardOption) {
      return (
        <Tooltip title={PhoneTooltipContent} placement="bottom">
          <span className={nameWithHover}>
            <UserHoverCard
              user={messageUser}
              messageContent={messageContent}
              userName={resolvedName}
              messageId={messageId}
              messageType={messageType}
              messageNotesCount={messageNotesCount}
              placement="topLeft"
            >
              <span>{resolvedName}</span>
            </UserHoverCard>
          </span>
        </Tooltip>
      );
    }

    return (
      <Tooltip title={PhoneTooltipContent}>
        <span className={nameClasses}>{resolvedName}</span>
      </Tooltip>
    );
  };

  // ---------- Lead Name (FROM_LEAD) ----------
  const renderLeadName = () => {
    const resolvedName = displayName || leadName;
    return (
      <Tooltip title={PhoneTooltipContent}>
        <span
          title={resolvedName}
          className="text-brand-500 text-xs line-clamp-1 break-all max-w-[180px] font-[600]"
        >
          {resolvedName}
        </span>
      </Tooltip>
    );
  };

  // ---------- Timestamp ----------
  const renderTimestamp = () => {
    const dateValue = timestamp;
    return (
      <Tooltip title={convertUtcToLocal(dateValue, timestampFullFormat)}>
        <span className="text-xs font-[400] text-grey-500">
          {convertUtcToLocal(dateValue, timestampFormat)}
        </span>
      </Tooltip>
    );
  };

  // ---------- Reason for Response ----------
  const renderReasonForResponse = () => {
    if (!reasonForResponse) return null;

    const icon = (
      <Tooltip
        title={reasonForResponse}
        overlayInnerStyle={{
          width: "auto",
          minWidth: "min(480px, 90vw)",
          maxWidth: "min(720px, 90vw)",
          maxHeight: "min(420px, 60vh)",
          overflowY: "auto",
          padding: "10px 14px",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        <span style={{ display: "inline-flex" }}>
          <QuestionCircleOutlined className="text-grey-500" />
        </span>
      </Tooltip>
    );

    const label = (
      <span className="text-xs font-[400] text-grey-500 hidden group-hover/reason:inline">
        Otto responded
      </span>
    );

    return (
      <span className="group/reason inline-flex items-center gap-1">
        {isSent ? (
          <>
            {label}
            {icon}
          </>
        ) : (
          <>
            {icon}
            {label}
          </>
        )}
      </span>
    );
  };

  // ---------- Tags Row (Campaign + Pinned) ----------
  const renderTags = () => {
    if (!hasTags) return null;
    return (
      <div className="flex items-center gap-2">
        {hasCampaign && (
          <span className="py-[2px] px-[10px] bg-orange-500 rounded-[6px] text-[12px] whitespace-nowrap">
            {campaign.name}
          </span>
        )}
        {isMessagePinned && (
          <PinnedMessageTag
            messageNotesCount={messageNotesCount}
            messageId={messageId}
            messageContent={messageContent}
            messageType={messageType}
            user={messageUser}
          />
        )}
      </div>
    );
  };

  // ========== RENDER ==========
  return (
    <div className="flex items-start">
      {/* Avatar LEFT (FROM_LEAD) */}
      {isReceived && (
        <div className="msg-avatar-left">
          {renderAvatar()}
          {showTranslationIcon && RenderTranslation && <RenderTranslation />}
        </div>
      )}

      {/* Content column */}
      <div className={isSent ? "msg-content-col-end" : "msg-content-col"}>
        {/* Header row */}
        <div
          className={`msg-timestamp-row flex-wrap ${
            isSent ? (hasTags ? "justify-end lg:justify-between" : "justify-end") : "justify-start"
          }`}
        >
          {/* Tags (left side for TO_LEAD) */}
          {isSent && renderTags()}

          {/* Header items */}
          <div className="msg-header-row">
            {/* FROM_LEAD order: name, timestamp, icon, [opp badge], [emailStatus], [reason] */}
            {isReceived && (
              <>
                {renderLeadName()}
                {renderTimestamp()}
                {renderMessageTypeIcon()}
                {opportunityBadge}
                {emailStatus && renderEmailStatus(emailStatus)}
                {renderReasonForResponse()}
              </>
            )}

            {/* TO_LEAD order: [emailStatus], [opp badge], icon, timestamp, name, [reason] */}
            {isSent && (
              <>
                {renderReasonForResponse()}
                {emailStatus && renderEmailStatus(emailStatus)}
                {opportunityBadge}
                {renderMessageTypeIcon()}
                {renderTimestamp()}
                {renderUserName()}
              </>
            )}
          </div>
        </div>

        {/* Message content (children) — faded while pending send */}
        <div
          style={
            isSending
              ? { opacity: 0.6, transition: "opacity 200ms ease-in-out" }
              : { transition: "opacity 200ms ease-in-out" }
          }
        >
          {children}
        </div>

        {/* Sending indicator: small italic note while waiting for the
            persisted Message to arrive via SocketIO sent_message event. */}
        {isSending && (
          <div className="flex items-center gap-1 text-[11px] italic text-black-45 mt-1">
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{
                backgroundColor: "#9CA3AF",
                animation: "smai-pulse 1.2s ease-in-out infinite",
              }}
            />
            <span>Sending…</span>
            <style>{`
              @keyframes smai-pulse {
                0%, 100% { opacity: 0.4; }
                50%      { opacity: 1; }
              }
            `}</style>
          </div>
        )}

        {/* Footer (action buttons) */}
        {(aiFeedbackProps || showSentButton || showRetry) && (
          <div className="flex w-full items-center justify-between self-stretch">
            {aiFeedbackProps ? (
              <div className="self-start">
                <AiFeedbackButtons {...aiFeedbackProps} />
              </div>
            ) : (
              <div />
            )}
            {showSentButton || showRetry ? (
              <div className="flex items-center gap-2">
                {showSentButton && (
                  <Button
                    icon={<EyeOutlined />}
                    type="text"
                    size="small"
                    className="!text-black-45"
                    onClick={onSentClick}
                  >
                    Sent
                  </Button>
                )}
                {showRetry && RetryComponent && (
                  <span className="text-[11px] mb-[2px]">{RetryComponent}</span>
                )}
              </div>
            ) : (
              <div />
            )}
          </div>
        )}
      </div>

      {/* Avatar RIGHT (TO_LEAD) */}
      {isSent && (
        <div className="msg-avatar-right">
          {renderAvatar()}
          {showTranslationIcon && RenderTranslation && <RenderTranslation />}
        </div>
      )}
    </div>
  );
};

export default MessageMetaInfo;
